import debounce from "lodash.debounce";
import PropTypes from "prop-types";
import * as React from "react";

import {
    BabelPropertyDescriptor,
    createPropertyOrMethodDecorator,
} from "../FormContext/DecoratorsContext/DecoratorsCompatibility";
import { isModelPath } from "../../../../../Common/ModelPath/ModelPath";

import { MetricsProviderContextProvider, MetricValue, TrackCallback } from "./MetricsProviderContext";
import { MetricsCollector } from "./MetricsCollector";

declare global {
    interface Window {
        _paq: unknown[];
    }
}

export interface MetricsActionPayload {
    [key: string]: unknown;
}

export interface MetricsPayload {
    [key: string]: MetricValue;
}

type TrackArgumentsTuple = [string, string, MetricsPayload];

export function track(category: string, action: string, payload: MetricsPayload = {}): void {
    payload = {
        id: action,
        ...payload,
    };
    for (const key of Object.keys(payload)) {
        if (isModelPath(payload[key])) {
            // @ts-ignore
            payload[key] = payload[key].toLegacyPath();
        }
    }
    if (window._paq) {
        if (!category) {
            throw new Error("Param category is required");
        }
        if (!action) {
            throw new Error("Param action is required");
        }
        const customVariables = MetricsCollector.addBasePayload(payload);
        addMetrics([category, action, customVariables]);
    }
}

const addMetrics = debouncedMerge<TrackArgumentsTuple>(
    metrics =>
        metrics.forEach(([category, action, options]) => {
            window._paq.push(["trackEvent", category, action, "options", JSON.stringify(options)]);
        }),
    500,
    getKey
);

function getKey([category, action, options]: TrackArgumentsTuple): string {
    const optionsStr = JSON.stringify(options);

    return `${category}/${action}/${optionsStr}`;
}

function debouncedMerge<TArg>(
    fn: (requests: TArg[]) => void,
    interval: number,
    getArgKey: (arg: TArg) => string
): (input: TArg) => void {
    let pendingRequests = new Map<string, TArg>();

    const debouncedFn = debounce(() => {
        fn([...pendingRequests.values()]);
        pendingRequests = new Map<string, TArg>();
    }, interval);

    return (arg: TArg): void => {
        const key = getArgKey(arg);
        if (pendingRequests.has(key)) {
            return;
        }

        pendingRequests.set(key, arg);
        debouncedFn();
    };
}

interface Props {
    children: React.ReactNode;
    track: TrackCallback;
}

interface MetricsProviderPushContext {
    track: TrackCallback;
}

export class MetricsProvider extends React.Component<Props> {
    public static childContextTypes = { track: PropTypes.func };

    public getChildContext(): MetricsProviderPushContext {
        return {
            track: this.props.track,
        };
    }

    public render(): JSX.Element {
        return (
            <MetricsProviderContextProvider value={this.props.track}>
                {this.props.children}
            </MetricsProviderContextProvider>
        );
    }
}

export function trackAction<P, S, CP extends unknown[]>(
    action: string,
    optionSelector?: (props: P, state: S, ...args: CP) => { [key: string]: unknown }
): MethodDecorator & PropertyDecorator {
    return createPropertyOrMethodDecorator(function decorator(
        target: React.Component<P, S>,
        _,
        descriptor: BabelPropertyDescriptor<React.Component<P, S>, unknown>
    ): BabelPropertyDescriptor<React.Component<P, S>, unknown> {
        // @ts-ignore
        target.constructor.contextTypes = { ...target.constructor.contextTypes, track: PropTypes.func };

        if ("value" in descriptor) {
            const originalFunction = descriptor.value;
            descriptor.value = function value(this: React.Component<P, S>, ...args: CP) {
                if (this.context.track) {
                    const options = {
                        // @ts-ignore
                        tid: this.props["data-tid"],
                        ...(optionSelector ? optionSelector(this.props, this.state, ...args) : {}),
                    };
                    this.context.track(action, options);
                } else {
                    // eslint-disable-next-line no-console
                    console.warn("Need wrap application MetricsProvider");
                }
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return originalFunction.apply(this, args);
            };
        } else if ("initializer" in descriptor) {
            const originalInitializer = descriptor.initializer;
            descriptor.initializer = function initializer(this: React.Component<P, S>, ...initializerArgs: []) {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const self = this;
                // @ts-ignore
                const originalFunction = originalInitializer.apply(this, initializerArgs);
                return function value(this: React.Component<P, S>, ...args: CP) {
                    if (self.context.track) {
                        const options = {
                            // @ts-ignore
                            tid: self.props["data-tid"],
                            ...(optionSelector ? optionSelector(self.props, self.state, ...args) : {}),
                        };
                        self.context.track(action, options);
                    } else {
                        // eslint-disable-next-line no-console
                        console.warn("Need wrap application MetricsProvider");
                    }
                    // @ts-ignore
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                    return originalFunction.apply(this, args);
                };
            };
        }

        return descriptor;
    });
}
